import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Newspaper } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function Home() {
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles").then((r) => {
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const items = (r.data || [])
        .filter((a) => a.published !== false && a.created_at && new Date(a.created_at).getTime() >= cutoff)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecent(items);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-nasa-navy">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-nasa-navy via-nasa-navy/80 to-nasa-navy/30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
            <span className="eyebrow text-white/90">PLATAFORMA DE DIVULGACIÓN CIENTÍFICA</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-hero font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl max-w-4xl leading-[1.05] text-white"
          >
            El Portal de La Ciencia
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-7 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed"
          >
            Aprende matemáticas y física y mantente al día con las últimas noticias de la ciencia.
          </motion.p>
        </div>
      </section>

      {/* Noticias recientes — vista rápida del último mes */}
      <section className="bg-white border-t border-paper-200" data-testid="home-recent-news">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <div className="flex items-end justify-between gap-4 mb-9">
            <div>
              <div className="flex items-center gap-3 mb-2"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">EL NOTICIERO CIENTÍFICO</span></div>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-nasa-blue">Noticias recientes</h2>
            </div>
            <Link to="/noticiero" data-testid="home-ver-todas" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-nasa-blue hover:gap-2.5 transition-all">
              Ver todas <ArrowRight size={15} />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => <div key={i} className="h-72 bg-paper-100 animate-pulse" />)}
            </div>
          ) : recent.length === 0 ? (
            <div className="flex flex-col items-center text-center border border-dashed border-paper-300 bg-paper-50 py-14 px-6" data-testid="home-no-news">
              <Newspaper className="text-ink-400 mb-3" size={30} />
              <p className="text-ink-600">Aún no hay noticias del último mes.</p>
              <Link to="/noticiero" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-nasa-blue hover:gap-2.5 transition-all">
                Explora El Noticiero <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {recent.map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Link to={`/noticiero/${a.id}`} data-testid={`home-news-${a.id}`} className="group block bg-white border border-paper-200 hover:border-nasa-blue transition-colors h-full">
                    {a.cover_image ? (
                      <div className="h-44 overflow-hidden">
                        <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="h-44 bg-nasa-navy flex items-center justify-center"><Newspaper className="text-white/40" size={34} /></div>
                    )}
                    <div className="p-5">
                      <span className="eyebrow text-ink-700 text-[10px] flex items-center gap-1.5"><Calendar size={11} /> {fmtDate(a.created_at)}</span>
                      <h3 className="font-serif-sci font-bold text-xl mt-1.5 text-nasa-blue group-hover:text-nasa-bright transition-colors line-clamp-2 leading-snug">{a.title}</h3>
                      {a.excerpt && <p className="text-ink-700 text-[15px] mt-2 line-clamp-3">{a.excerpt}</p>}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-8 sm:hidden">
            <Link to="/noticiero" className="inline-flex items-center gap-1.5 text-sm font-bold text-nasa-blue">Ver todas las noticias <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-white border-t border-paper-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid sm:grid-cols-3 gap-10">
          {[
            { t: "Rigor científico", d: "Hecho por un matemático y estudiante de Física. Desde un punto de vista objetivo y pragmático." },
            { t: "Aprendizaje activo", d: "Practica con problemas y revela la solución cuando quieras." },
            { t: "Siempre actual", d: "Noticias científicas redactadas y actualizadas al momento." },
          ].map((f, i) => (
            <div key={f.t} className="flex gap-4">
              <span className="shrink-0 flex h-12 w-12 items-center justify-center text-nasa-blue font-scientific font-bold text-xl">{["I.", "II.", "III."][i]}</span>
              <div>
                <h4 className="font-display font-bold text-lg text-nasa-blue">{f.t}</h4>
                <p className="text-ink-700 text-[15px] mt-1">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
